import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './RexiTooltip.css';

type RexiTooltipProps = {
  message: string;
  visible: boolean;
  position?: 'top' | 'bottom' | 'left' | 'right';
  children: any;
};

function RexiTooltip(props: RexiTooltipProps) {
  const { message, visible, position = 'top', children } = props;
  const wrapperRef = useRef(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [showTooltip, setShowTooltip] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [internalHover, setInternalHover] = useState(false);

  useEffect(() => {
    let showTimer: NodeJS.Timeout | undefined;
    let hideTimer: NodeJS.Timeout | undefined;
    let fadeTimer: NodeJS.Timeout | undefined;
    
    // ONLY SHOW IF BOTH CONDITIONS TRUE: parent says visible AND mouse is actually hovering
    if (visible && internalHover) {
      showTimer = setTimeout(() => {
        if (wrapperRef.current) {
          const rect = wrapperRef.current.getBoundingClientRect();
          const tooltipWidth = 250;
          
          let style = {};
          
          if (position === 'top') {
            style = {
              position: 'fixed',
              bottom: window.innerHeight - rect.top + 8,
              left: rect.left + (rect.width / 2) - (tooltipWidth / 2),
              zIndex: 9999
            };
          }
          
          setTooltipStyle(style);
          setShowTooltip(true);
          
          // Trigger fade-in after a brief delay to ensure element is rendered
          fadeTimer = setTimeout(() => {
            setIsAnimating(true);
          }, 10);
        }
      }, 500);
    } else {
      // FORCE CLOSE if parent says not visible OR mouse not hovering
      setIsAnimating(false);
      hideTimer = setTimeout(() => {
        setShowTooltip(false);
      }, 600); // Allow fade-out to complete
    }
    
    return () => {
      if (showTimer) clearTimeout(showTimer);
      if (hideTimer) clearTimeout(hideTimer);
      if (fadeTimer) clearTimeout(fadeTimer);
    };
  }, [visible, internalHover, position]);

  // Force cleanup tooltip state on any global click to prevent stuck tooltips
  useEffect(() => {
    const handleGlobalClick = () => {
      if (showTooltip && !visible) {
        setShowTooltip(false);
        setIsAnimating(false);
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [showTooltip, visible]);

  const tooltipContent = showTooltip ? (
    <div className={`rexi-tooltip rexi-tooltip-${position} ${isAnimating ? 'rexi-tooltip-visible' : 'rexi-tooltip-hidden'}`} style={tooltipStyle}>
      <div className="rexi-tooltip-content">
        <img src="/rexi-logo.png" alt="Rexi" className="rexi-tooltip-logo" />
        <div className="rexi-tooltip-text">{message}</div>
      </div>
    </div>
  ) : null;

  return (
    <div 
      className="rexi-tooltip-wrapper" 
      ref={wrapperRef}
      onMouseEnter={() => setInternalHover(true)}
      onMouseLeave={() => setInternalHover(false)}
    >
      {children}
      {tooltipContent && createPortal(tooltipContent, document.body)}
    </div>
  );
}

export default RexiTooltip;